import { BookOpenIcon } from '@heroicons/react/24/outline';
import env from '../../../environment';
import type { FeaturePageDataInterface } from './types';

// Pillar page for web crawling / market research. Reuses the feature-page
// structure but is deliberately kept out of `featurePages` so it doesn't
// appear in the features dropdown, footer column, or homepage grid.
export const webCrawlingForResearch: FeaturePageDataInterface = {
  slug: 'web-crawling-for-research',
  navTitle: 'What is web crawling for research?',
  shortTitle: 'Web crawling',
  navDescription:
    'How Stark Crawl turns directories and websites into structured data and prospect lists for market research.',
  Icon: BookOpenIcon,
  headline: 'What is',
  highlight: 'web crawling for market research?',
  blurb:
    'Web crawling for market research means systematically discovering hosts, fetching pages, and extracting the structured fields you care about — company details, contacts, products, and other prospect signals — so analysts get research-ready data instead of one-off scrapes.',
  quote: {
    text: 'We stopped babysitting brittle scrapers and started shipping prospect lists from directories the same week we signed up.',
    author: 'Customer name',
    authorRole: 'Job title, Company',
  },
  image: {
    src: '/images/shots/coverage-segment.png',
    alt: 'Stark Crawl discovering hosts and extracting structured prospect data',
  },
  content: [
    {
      type: 'block',
      body: {
        heading: 'From open web to research-ready data',
        body: [
          'Market research teams already know where prospects live — directories, listings, competitor sites, and niche vertical pages. The hard part is getting structured data out at scale without a custom scraping codebase.',
          'Stark Crawl closes that gap. It discovers hosts, crawls pages, extracts fields you define, and captures prospects into projects your team can review and export.',
        ],
        large: true,
      },
    },
    {
      type: 'block',
      body: {
        heading: 'Not a generic scraper, not a spreadsheet ritual',
        body: [
          'A one-off scraper breaks when a layout changes and usually lives on one engineer’s laptop. A research crawl should be a shared project: targets, schemas, and captures that the whole team can run and trust.',
          'Generic web search returns links to read. Stark Crawl reads and extracts for you — turning pages into structured rows ready for analysis, outreach, or enrichment.',
        ],
      },
    },
    {
      type: 'block',
      body: {
        heading: 'How it works',
        body: [
          'Define what you are looking for: markets, categories, host sources, and the fields that make a prospect useful. Stark Crawl discovers hosts, crawls pages, and extracts structured data against your schema.',
          'From there, your team reviews captures, refines rules, and exports research-ready lists — without rebuilding scrapers for every new directory.',
        ],
      },
    },
    {
      type: 'block',
      body: {
        heading: 'Built for research teams',
        body: [
          'Run separate projects per market or segment. Share access with analysts and operators. Pace crawls with rate limits so runs stay predictable.',
          'Use it for directory coverage, competitor monitoring, lead lists, category mapping, and other market-research workflows that start on the open web.',
        ],
      },
    },
    {
      type: 'block',
      body: {
        heading: 'Is crawl data safe?',
        body: [
          'Your projects and extracted data live in an isolated environment dedicated to your account, encrypted in transit and at rest, and are never used to train AI models.',
        ],
      },
    },
    {
      type: 'solution',
      body: 'Discover hosts, crawl pages, extract structured fields, and capture prospects — market research data without a custom scraping stack.',
      cta: 'Try for free',
      url: env.appUrl + '/register',
    },
  ],
  seo: {
    title: 'What is web crawling for market research? - Stark Crawl',
    description:
      'How Stark Crawl turns directories and websites into structured data and prospect lists for market research — discovery, crawl, extract, capture.',
    keywords:
      'web crawling, market research crawl, structured data extraction, prospect capture, directory crawl, host discovery, Stark Crawl',
  },
  ctaTitle: 'Turn the open web into research data',
  ctaBody:
    'Discover hosts, crawl pages, extract structured fields, and capture prospects — without building your own scrapers.',
};
